/**
 * MindGraphView — vibe-designer WS-B1 (2026-07-12).
 *
 * Per-service viz on the MIND detail panel: a document-level **similarity
 * graph** (Phase B). Calls `GET /api/knowledge/services/mind/graph` →
 * `{nodes:{id,name,group,edge_degree}, edges:{source,target,label}}` (same
 * shape as CORTEX) and renders the SAME 2D/3D force-graph canvases the
 * CORTEX view uses. Nodes = distinct documents; edges = document pairs with
 * a high embedding cosine-similarity (label "sim 0.NN"); node group = pillar.
 *
 * Mirrors CortexGraphView (2D/3D toggle default 3D, lazy per-dimension
 * chunk, honest empty/error branches) but reads the MIND graph store slice.
 */
import { useEffect, useMemo, useState, Suspense, lazy } from 'react';
import { Loader2, RefreshCw, Share2 } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';
import {
  DEFAULT_MIN_STRENGTH,
  DEFAULT_SPACING,
  edgeAlphaForCount,
  visibleCounts,
} from './knowledgeGraphStyle';

const ForceGraphCanvas2D = lazy(() => import('./_KnowledgeSigmaCanvas'));
const ForceGraphCanvas3D = lazy(() => import('./_CortexForceGraph3DCanvas'));

const DIM_STORAGE_KEY = 'vibe_designer_mind_graph_dim';

function readInitialDim(): '2d' | '3d' {
  try {
    return localStorage.getItem(DIM_STORAGE_KEY) === '2d' ? '2d' : '3d';
  } catch {
    return '3d';
  }
}

export function MindGraphView() {
  const graph = useKnowledgeStore((s) => s.mindGraph);
  const isFetching = useKnowledgeStore((s) => s.isFetchingMindGraph);
  const error = useKnowledgeStore((s) => s.mindGraphError);
  const fetchMindGraph = useKnowledgeStore((s) => s.fetchMindGraph);
  // Re-fetch on SSE ingest event (new docs change the similarity graph).
  const lastIngestAt = useKnowledgeStore((s) => s.lastIngestAt);
  const [dimension, setDimension] = useState<'2d' | '3d'>(readInitialDim);

  // Graph Explorer controls (2026-07-24). Strength filter → smaller high-value
  // map; show-all bypasses it; spacing = collide buffer; edge brightness = line
  // alpha. Passed to the canvas; the canvas reads them live (no rebuild).
  const [minStrength, setMinStrength] = useState(DEFAULT_MIN_STRENGTH);
  const [showAll, setShowAll] = useState(false);
  const [spacing, setSpacing] = useState(DEFAULT_SPACING);
  const [edgeBrightness, setEdgeBrightness] = useState<number | null>(null); // null → auto

  const edgeCount = graph?.edges.length ?? 0;
  const heavyGraph = (graph?.nodes.length ?? 0) > 800;
  // Brightness resolves per dimension: 3D drives three.js linkOpacity (0..1,
  // dimmer default on a heavy graph); 2D drives the density-aware haze alpha.
  const autoBrightness =
    dimension === '3d' ? (heavyGraph ? 0.2 : 0.4) : edgeAlphaForCount(edgeCount);
  const effectiveBrightness = edgeBrightness ?? autoBrightness;
  const brightnessMax = dimension === '3d' ? 1 : 0.2;
  const brightnessStep = dimension === '3d' ? 0.02 : 0.005;

  const counts = useMemo(
    () =>
      graph && graph.nodes.length > 0
        ? visibleCounts(graph.nodes, graph.edges, minStrength, showAll)
        : { nodes: 0, edges: 0 },
    [graph, minStrength, showAll],
  );

  useEffect(() => {
    void fetchMindGraph();
  }, [fetchMindGraph, lastIngestAt]);

  useEffect(() => {
    try {
      localStorage.setItem(DIM_STORAGE_KEY, dimension);
    } catch {
      // best-effort persistence; in-memory state stays correct
    }
  }, [dimension]);

  // Each dimension has its own brightness auto-default (2D haze alpha vs 3D
  // linkOpacity), so drop any operator override when the dimension flips.
  useEffect(() => {
    setEdgeBrightness(null);
  }, [dimension]);

  return (
    <section className="rounded border border-vibe-border bg-vibe-bg-secondary p-3 mb-4">
      <header className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary flex items-center gap-1.5">
          <Share2 className="w-3 h-3" />
          Document similarity graph
          {graph && graph.nodes.length > 0 && (
            <span data-testid="mind-graph-counts" className="ml-2 text-vibe-text-secondary tabular-nums">
              {showAll ? graph.nodes.length : counts.nodes}/{graph.nodes.length} docs · {showAll ? graph.edges.length : counts.edges}/{graph.edges.length} links
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <div
            role="group"
            aria-label="Graph dimension"
            className="flex items-center gap-0.5 rounded bg-vibe-bg-tertiary p-0.5"
          >
            {(['2d', '3d'] as const).map((dim) => (
              <button
                key={dim}
                data-testid={`mind-graph-toggle-${dim}`}
                type="button"
                onClick={() => setDimension(dim)}
                aria-pressed={dimension === dim}
                className={
                  dimension === dim
                    ? 'px-2 py-0.5 text-[10px] font-semibold rounded bg-vibe-accent text-vibe-bg'
                    : 'px-2 py-0.5 text-[10px] font-semibold rounded text-vibe-text-secondary hover:text-vibe-text'
                }
              >
                {dim.toUpperCase()}
              </button>
            ))}
          </div>
          <button
            data-testid="mind-graph-refresh"
            type="button"
            onClick={() => void fetchMindGraph()}
            disabled={isFetching}
            className="px-2 py-1 text-xs rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary disabled:opacity-50 flex items-center gap-1"
          >
            {isFetching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            <span>Refresh</span>
          </button>
        </div>
      </header>

      {error && (
        <div
          data-testid="mind-graph-network-error"
          className="rounded border border-vibe-error/30 bg-vibe-error/10 p-2 text-xs text-vibe-error"
        >
          {error.message}
        </div>
      )}

      {!error && graph && graph.error && (
        <div
          data-testid="mind-graph-error"
          className="rounded border border-vibe-warning/30 bg-vibe-warning/10 p-2 text-xs text-vibe-warning"
        >
          {graph.error}
        </div>
      )}

      {!error && graph && !graph.error && graph.nodes.length === 0 && (
        <div
          data-testid="mind-graph-empty"
          className="text-xs text-vibe-text-secondary py-2"
        >
          No documents to relate yet — MIND has no embedded documents for this tenant.
        </div>
      )}

      {!error && graph && !graph.error && graph.nodes.length > 0 && (
        <>
          {/* Explorer controls (2D + 3D). Strength filter shrinks to a high-value
              map; show-all brings everything back; brightness dials the line
              alpha. Spacing is 2D-only — it de-overlaps the FIXED UMAP layout;
              the 3D force layout already separates nodes in depth. */}
          <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-2 rounded bg-vibe-bg-tertiary/50 px-2 py-1.5 text-[10px] text-vibe-text-secondary">
              <label className="flex items-center gap-1.5">
                <span className="whitespace-nowrap">Min strength</span>
                <input
                  data-testid="mind-graph-strength"
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={minStrength}
                  disabled={showAll}
                  onChange={(e) => setMinStrength(parseFloat(e.target.value))}
                  className="w-24 accent-vibe-accent disabled:opacity-40"
                />
                <span className="tabular-nums w-8">{minStrength.toFixed(2)}</span>
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  data-testid="mind-graph-show-all"
                  type="checkbox"
                  checked={showAll}
                  onChange={(e) => setShowAll(e.target.checked)}
                  className="accent-vibe-accent"
                />
                <span>Show all nodes</span>
              </label>
              {dimension === '2d' && (
                <label className="flex items-center gap-1.5">
                  <span>Spacing</span>
                  <input
                    data-testid="mind-graph-spacing"
                    type="range"
                    min={0}
                    max={40}
                    step={1}
                    value={spacing}
                    onChange={(e) => setSpacing(parseFloat(e.target.value))}
                    className="w-24 accent-vibe-accent"
                  />
                  <span className="tabular-nums w-6">{spacing}</span>
                </label>
              )}
              <label className="flex items-center gap-1.5">
                <span className="whitespace-nowrap">Line brightness</span>
                <input
                  data-testid="mind-graph-brightness"
                  type="range"
                  min={0}
                  max={brightnessMax}
                  step={brightnessStep}
                  value={effectiveBrightness}
                  onChange={(e) => setEdgeBrightness(parseFloat(e.target.value))}
                  className="w-24 accent-vibe-accent"
                />
                <span className="tabular-nums w-10">{effectiveBrightness.toFixed(3)}</span>
              </label>
            </div>
          {dimension === '3d' && (
            <p className="mb-2 text-[10px] text-vibe-text-secondary/70">
              Click a node to focus its links · click empty space to clear
            </p>
          )}
          <div
            data-testid="mind-graph-canvas"
            className="aspect-square w-full bg-vibe-bg rounded overflow-hidden border border-vibe-border"
          >
            <Suspense fallback={<div className="text-xs text-vibe-text-secondary p-2">Loading graph…</div>}>
              {dimension === '3d' ? (
                <ForceGraphCanvas3D
                  nodes={graph.nodes}
                  edges={graph.edges}
                  minStrength={minStrength}
                  showAll={showAll}
                  edgeBrightness={effectiveBrightness}
                />
              ) : (
                <ForceGraphCanvas2D
                  nodes={graph.nodes}
                  edges={graph.edges}
                  minStrength={minStrength}
                  showAll={showAll}
                  spacing={spacing}
                  edgeBrightness={effectiveBrightness}
                />
              )}
            </Suspense>
          </div>
        </>
      )}
    </section>
  );
}

export default MindGraphView;
